import * as React from 'react'
import { View, Text } from 'react-native'
import styled from '@theme/styled-components'

import TextInput from '@components/textInput/TextInput'

import useI18n from '@i18n/useI18n'

import useReducer from '@store/useReducer'
import * as AuthStore from '@store/auth/auth'

import { FormikHelpers, useFormik } from 'formik'

import { AuthScope } from '@api/enum'

interface Props {
  user: UserResponse
  form: UserUpdateRequest
  onChange: (values: UserUpdateRequest) => Promise<void>
}

const Info = ({ user, form, onChange }: Props) => {
  const i18n = useI18n()

  const scopes = useReducer(AuthStore.store, (s) => s.scopes)

  const onSubmit = (vals: UserUpdateRequest, { setSubmitting }: FormikHelpers<UserUpdateRequest>) => {
    onChange(vals).finally(() => setSubmitting(false))
  }

  const { values, setFieldValue, handleSubmit, dirty } = useFormik<UserUpdateRequest>({
    enableReinitialize: true,
    initialValues: form,
    onSubmit,
  })

  React.useEffect(() => {
    if (dirty) {
      // sumbit on change
      handleSubmit()
    }
  }, [values])

  const renderField = (item: keyof UserUpdateRequest) => {
    return (
      <ItemContainer key={item}>
        <ItemTitle>{i18n.t(`screens.account.generalSection.items.${item}`)}</ItemTitle>
        <TextInput
          onChange={(text) => setFieldValue(item, text)}
          placeholder={i18n.t(`screens.account.generalSection.items.${item}`)}
          value={values[item] || ''}
          disabled={!scopes.includes(AuthScope.UtilisateursInformationModification)}
        />
      </ItemContainer>
    )
  }

  const renderInfo = (item: keyof UserResponse) => (
    <ItemContainer key={item}>
      <ItemTitle>{i18n.t(`screens.account.generalSection.items.${item}`)}</ItemTitle>
      <FieldValue disabled>{(user[item] as string) || ' '}</FieldValue>
    </ItemContainer>
  )

  return (
    <MainContainer>
      <Body>
        {renderField('prenom')}
        {renderField('nom')}
        {renderInfo('codeMagic')}
        {renderField('fonction')}
        {renderInfo('email')}
        {renderField('numeroTelephone')}
      </Body>
    </MainContainer>
  )
}

export default Info

const MainContainer = styled(View)`
  padding: 12px 24px;
`

const Body = styled(View)`
  flex-flow: ${(props) => (props.theme.breakpoint === 'phone' ? 'column' : 'row')}
    ${(props) => (props.theme.breakpoint !== 'phone' ? 'wrap' : 'nowrap')};
`

const ItemContainer = styled(View)`
  margin-right: ${(props) => (props.theme.breakpoint !== 'phone' ? 24 : 0)}px;
  ${(props) => props.theme.breakpoint !== 'phone' && 'width: 370px;'};
  margin-top: 16px;
`

const ItemTitle = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  color: ${(props) => props.theme.colors.secondaryText};
  margin-bottom: 8px;
`

const FieldValue = styled(Text)<{ disabled?: boolean }>`
  ${(props) => props.theme.fonts.Body};
  min-height: ${(props) => (props.theme.breakpoint === 'phone' ? 48 : 56)}px;

  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? `
        padding: 14px 16px;
        background-color: ${props.theme.colors.background};
        border-bottom-width: 1px;
        border-bottom-color: ${props.theme.colors.primaryFocused};
        ${props.disabled && `background-color: ${props.theme.colors.secondaryActive}; opacity: 0.5;`}
        `
      : `
        padding: 14px 16px;
        border-radius: 6px;
        border: 1px solid ${props.theme.colors.secondaryActive};
        ${props.disabled && `background-color: ${props.theme.colors.secondaryActive}; opacity: 0.5;`}`}
`
